import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  doc, getDoc, collection, setDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import {
  uploadImage, inspectImage, friendlyMessage, logStorageFailure,
} from '../firebase/uploadImage';
import PhoneInput from '../components/PhoneInput';
import { TfrxHeader, TfrxFooter } from '../components/TfrxChrome';

const KERALA_DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Calicut', 'Wayanad', 'Kannur', 'Kasaragod',
];
const FRONX_VARIANTS = [
  'Sigma', 'Delta', 'Delta+', 'Delta+ O', 'Zeta', 'Alpha',
];
const FRONX_COLORS = [
  'Nexa Blue', 'Splendid Silver', 'Grandeur Grey', 'Opulent Red',
  'Arctic White', 'Earthen Brown', 'Cafe Noir (Dual Tone)',
  'Bluish Black (Dual Tone)',
];
const BLOOD_GROUPS = [
  'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown',
];
const ENGINE_VARIANTS = [
  '1.2L Petrol Manual', '1.2L Petrol Automatic',
  '1.2L CNG Manual',    '1.2L CNG Automatic',
  '1.0L Turbo Petrol Manual', '1.0L Turbo Petrol Automatic',
];

const INITIAL = {
  name:              '',
  phone_number:      '+91',
  email:             '',
  district:          '',
  car_variant:       '',
  car_color:         '',
  vehicle_number:    '',
  blood_group:       '',
  emergency_contact: '+91',
  engine_variant:    '',
  // shipping address (only collected for Standard / Premium)
  house_no:          '',
  address_line:      '',
  landmark:          '',
  city:              '',
  state:             '',
  pincode:           '',
  alternate_phone:   '+91',
};

// India - 28 states + 8 union territories. Single flat list so the
// signup form, admin panel, and downstream Sheet exports all agree.
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
  // Union Territories
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi',
  'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const STEP_TIER    = 'tier';
const STEP_FORM    = 'form';
const STEP_PAY     = 'pay';
const STEP_SUCCESS = 'success';

const TIER_META = {
  basic: {
    label:    'Basic Member',
    tagline:  'Community access only',
    features: [
      'Full member profile',
      'WhatsApp group invite',
      'No physical logo · no app access',
    ],
    needsShipping: false,
    needsAppAccess: false,
  },
  standard: {
    label:    'Standard Member',
    tagline:  'Community + branded logo',
    features: [
      'Everything in Basic',
      'Physical Team Fronx logo shipped to you',
      'No mobile app access',
    ],
    needsShipping: true,
    needsAppAccess: false,
  },
  apponly: {
    label:    'Team Fronx Mobile Access',
    tagline:  'Mobile app access · no logo',
    features: [
      'Mobile app access · 1 year',
      'Android only · iPhone (iOS) coming soon',
      'AI-powered car care assistant',
      'Digital garage · service reminders',
      'Club events & member-only coupons',
      'Digital member ID card',
    ],
    needsShipping: false,
    needsAppAccess: true,
  },
  premium: {
    label:    'Premium Member',
    tagline:  'Full access',
    features: [
      'Everything in Basic',
      'Physical Team Fronx logo shipped to you',
      'Mobile app access · 1 year',
      'Android only · iPhone (iOS) coming soon',
      'Digital member ID card',
    ],
    needsShipping: true,
    needsAppAccess: true,
  },
};

export default function SignupPage() {
  // /signup/:tier (basic | standard | premium) skips the tier picker and
  // takes the user straight to the form. /signup with no tier (or an
  // unknown one) falls back to the picker as before.
  const { tier: tierParam } = useParams();
  const presetTier =
    tierParam && ['basic', 'standard', 'apponly', 'premium'].includes(tierParam.toLowerCase())
      ? tierParam.toLowerCase()
      : null;

  const [cfg,        setCfg]        = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [step,       setStep]       = useState(presetTier ? STEP_FORM : STEP_TIER);
  const [tier,       setTier]       = useState(presetTier);  // 'basic' | 'standard' | 'premium'
  const [appId,      setAppId]      = useState(null);
  const [form,       setForm]       = useState(INITIAL);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);

  // Screenshot upload state (Pay step)
  const [shotFile,   setShotFile]   = useState(null);
  const [shotPreview, setShotPreview] = useState('');
  const shotInputRef = useRef(null);
  const [uploading,  setUploading]  = useState(false);
  const [shotProgress, setShotProgress] = useState(0);

  // Profile photo upload state (Form step). The photo is uploaded BEFORE the
  // application doc is written, to signup_photos/{appId}.{ext} — see
  // handleSubmitForm.
  const [photoFile,   setPhotoFile]   = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoProgress, setPhotoProgress] = useState(0);
  const photoInputRef = useRef(null);

  // The application's document id is minted client-side and reused across
  // retries, so a failed submit can't leave a trail of half-filled
  // applications behind. `uploadedPath` remembers a photo that already landed
  // so a retry of a later step doesn't re-upload it (and get denied by the
  // no-overwrite rule).
  const appDocRef     = useRef(null);
  const uploadedPath  = useRef(null);

  const handlePhotoChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await inspectImage(file);
    if (!check.ok) {
      setErrors(p => ({ ...p, profile_photo: check.reason }));
      return;
    }
    setErrors(p => ({ ...p, profile_photo: undefined }));
    uploadedPath.current = null;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }, []);

  const clearPhoto = useCallback(() => {
    setPhotoFile(null);
    setPhotoPreview('');
    uploadedPath.current = null;
    if (photoInputRef.current) photoInputRef.current.value = '';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_config', 'membership'));
        if (snap.exists()) setCfg(snap.data());
      } catch { /* ignore */ }
      setCfgLoading(false);
    })();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    setErrors(p => ({ ...p, [name]: undefined }));
  }, []);

  const meta = tier ? TIER_META[tier] : null;

  const validate = useCallback(() => {
    const e = {};
    if (!photoFile) e.profile_photo = 'Profile photo is required.';
    if (!form.name.trim()) e.name = 'Full name is required.';
    // PhoneInput emits "+91" + (0-10 digits). Require all 10 digits.
    if (!/^\+91\d{10}$/.test(form.phone_number.trim()))
      e.phone_number = 'Enter a valid 10-digit mobile number.';
    if (!form.email.trim())
      e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = 'Enter a valid email.';
    if (!form.district)      e.district     = 'Select district.';
    if (!form.car_variant)   e.car_variant  = 'Select variant.';
    if (!form.car_color)     e.car_color    = 'Select colour.';
    if (!form.vehicle_number.trim()) e.vehicle_number = 'Vehicle number is required.';
    if (!form.blood_group)   e.blood_group  = 'Select blood group.';
    if (!form.engine_variant) e.engine_variant = 'Select engine variant.';
    // Emergency contact now required.
    const ec = form.emergency_contact.trim();
    if (!ec || ec === '+91')
      e.emergency_contact = 'Emergency contact is required.';
    else if (!/^\+91\d{10}$/.test(ec))
      e.emergency_contact = 'Enter a valid 10-digit number.';
    if (meta?.needsShipping) {
      if (!form.house_no.trim())    e.house_no    = 'House / flat number is required.';
      if (!form.address_line.trim()) e.address_line = 'Street / area is required.';
      if (!form.landmark.trim())     e.landmark    = 'Landmark is required.';
      if (!form.city.trim())         e.city         = 'City is required.';
      if (!form.state)               e.state        = 'Select state.';
      if (!/^\d{6}$/.test(form.pincode.trim()))
        e.pincode = '6-digit PIN code required.';
      const altp = form.alternate_phone.trim();
      if (!altp || altp === '+91')
        e.alternate_phone = 'Alternate phone is required.';
      else if (!/^\+91\d{10}$/.test(altp))
        e.alternate_phone = 'Enter a valid 10-digit number.';
    }
    return e;
  }, [form, meta, photoFile]);

  const tierPrice = useCallback((t) => {
    if (!cfg) return 0;
    if (t === 'basic')    return Number(cfg.tier_basic_price)    || 0;
    if (t === 'standard') return Number(cfg.tier_standard_price) || 0;
    if (t === 'apponly')  return Number(cfg.tier_apponly_price)  || 0;
    if (t === 'premium')  return Number(cfg.tier_premium_price)  || 0;
    return 0;
  }, [cfg]);

  const handleSubmitForm = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setPhotoProgress(0);
    try {
      const price = tierPrice(tier);

      // Mint the id locally (no write yet) so the photo can be uploaded to a
      // path derived from it BEFORE the application exists.
      if (!appDocRef.current) {
        appDocRef.current = doc(collection(db, 'membership_applications'));
      }
      const appRef = appDocRef.current;

      // Photo first, document second. The old order (addDoc → upload) left an
      // application with no photo behind every failed upload, and every retry
      // minted another one. This way a failed upload writes nothing at all, and
      // the worst case is an orphaned object — which the account-deletion and
      // purge jobs already sweep by the signup_photos/{id} prefix.
      if (!uploadedPath.current) {
        const res = await uploadImage(photoFile, {
          pathPrefix: 'signup_photos',
          id: appRef.id,
          onProgress: setPhotoProgress,
        });
        uploadedPath.current = res.path;
      }

      await setDoc(appRef, {
        ...form,
        phone_number:   form.phone_number.trim(),
        email:          form.email.trim().toLowerCase(),
        vehicle_number: form.vehicle_number.trim().toUpperCase(),
        house_no:       form.house_no.trim(),
        address_line:   form.address_line.trim(),
        landmark:       form.landmark.trim(),
        city:           form.city.trim(),
        state:          form.state,
        pincode:        form.pincode.trim(),
        // Save empty when user left only the +91 prefix.
        alternate_phone: form.alternate_phone.trim() === '+91'
          ? ''
          : form.alternate_phone.trim(),
        tier,
        amount_inr:     price,
        needs_shipping: meta?.needsShipping ?? false,
        grants_app_access: meta?.needsAppAccess ?? false,
        // A download URL can't be minted here: reading back an object is a
        // Storage `read`, which the public pages are not allowed to do. The
        // admin panel resolves this path when it reviews the application.
        profile_photo_path: uploadedPath.current,
        status:         price > 0 ? 'awaiting_payment' : 'awaiting_verification',
        submitted_at:   serverTimestamp(),
      });
      setAppId(appRef.id);

      // Free tier -> straight to success / admin review.
      if (price === 0) {
        setStep(STEP_SUCCESS);
      } else {
        setStep(STEP_PAY);
      }
    } catch (err) {
      logStorageFailure('signup_submit', err, { tier, docId: appDocRef.current?.id });
      setError(err.friendly || friendlyMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [form, validate, tier, meta, tierPrice, photoFile]);

  const handleShotChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await inspectImage(file);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    setError(null);
    setShotFile(file);
    setShotPreview(URL.createObjectURL(file));
  }, []);

  const submitScreenshot = useCallback(async () => {
    if (!shotFile || !appId) return;
    setUploading(true);
    setShotProgress(0);
    setError(null);
    try {
      const { path } = await uploadImage(shotFile, {
        pathPrefix: 'payment_screenshots',
        id: appId,
        onProgress: setShotProgress,
      });
      await updateDoc(doc(db, 'membership_applications', appId), {
        status:                 'awaiting_verification',
        payment_screenshot_path: path,
        screenshot_uploaded_at:  serverTimestamp(),
      });
      setStep(STEP_SUCCESS);
    } catch (err) {
      logStorageFailure('screenshot_submit', err, { docId: appId });
      setError(err.friendly || friendlyMessage(err));
    } finally {
      setUploading(false);
    }
  }, [shotFile, appId]);

  // `wide` = plan step: content spans the container (max ~1232px, matching the
  // header/footer, with equal dark margins). Otherwise the boxed 560px card.
  const wrap = (children, wide = false) => (
    <div style={{
      minHeight: '100vh',
      background: '#09090B',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <TfrxHeader />
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: wide ? 'flex-start' : 'center',
        justifyContent: 'center',
        padding: wide ? '40px 18px' : '40px 16px',
      }}>
        {wide ? (
          <div style={{ width: '100%', maxWidth: 1232, margin: '0 auto' }}>
            {children}
          </div>
        ) : (
          <div style={{
            maxWidth: 560, width: '100%',
            background: '#111113',
            border: '1px solid rgba(212, 181, 116, 0.2)',
            borderRadius: 16,
            padding: 28,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 80px rgba(212,181,116,0.08)',
          }}>
            {children}
          </div>
        )}
      </div>
      <TfrxFooter />
    </div>
  );

  if (cfgLoading) return wrap(
    <div style={{ textAlign: 'center', color: '#D8C8A4' }}>Loading…</div>
  );

  if (cfg && cfg.signup_enabled === false) return wrap(
    <div style={{ textAlign: 'center' }}>
      <img src="/logo.png" alt="" style={{ width: 80, height: 80, marginBottom: 16 }} />
      <h2 style={{ color: '#F7E8BF', fontSize: 20, marginBottom: 8 }}>
        Signups paused
      </h2>
      <p style={{ color: '#D8C8A4', fontSize: 14, lineHeight: 1.55 }}>
        Team Fronx is not accepting new members right now.
      </p>
    </div>
  );

  if (step === STEP_SUCCESS) return wrap(
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>⏳</div>
      <h2 style={{ color: '#F7E8BF', fontSize: 22, marginBottom: 10, fontWeight: 900 }}>
        Payment uploaded
      </h2>
      <p style={{ color: '#D8C8A4', fontSize: 14, lineHeight: 1.6, marginBottom: 14 }}>
        {cfg?.success_message ||
          "We'll verify your payment shortly and contact you with the WhatsApp link."}
      </p>
      <div style={{
        padding: 14,
        background: 'rgba(212, 181, 116, 0.08)',
        border: '1px solid rgba(212, 181, 116, 0.22)',
        borderRadius: 10,
        color: '#D4B574',
        fontSize: 12,
        marginBottom: 18,
      }}>
        Verification usually within <strong>24 hours</strong>. You'll get
        a WhatsApp message from an admin with the group invite link.
      </div>
    </div>
  );

  if (step === STEP_TIER) return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <img src="/logo.png" alt="" style={{ width: 90, height: 90, marginBottom: 10 }} />
        <h1 style={{ color: '#F7E8BF', fontSize: 34, fontWeight: 900, marginBottom: 6 }}>
          Join Team Fronx
        </h1>
        <p style={{ color: '#B79F6D', fontSize: 14, letterSpacing: 1.8, textTransform: 'uppercase' }}>
          Step 1 of 3 · Choose your plan
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: 24,
        alignItems: 'stretch',
      }}>
      {['basic', 'standard', 'premium', 'apponly'].map(t => {
        const m = TIER_META[t];
        const price = tierPrice(t);
        return (
          <button
            key={t}
            type="button"
            onClick={() => { setTier(t); setStep(STEP_FORM); }}
            style={{
              width: '100%',
              height: '100%',
              padding: 30,
              background: t === 'premium' ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2010 100%)' : '#0F0F0F',
              border: `1px solid ${t === 'premium' ? '#D4B574' : 'rgba(212,181,116,0.25)'}`,
              borderRadius: 16,
              textAlign: 'left',
              cursor: 'pointer',
              color: 'inherit',
              transition: 'transform 120ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span style={{ color: '#F7E8BF', fontSize: 22, fontWeight: 800 }}>
                {m.label}
              </span>
              <span style={{ color: '#D4B574', fontSize: 28, fontWeight: 900 }}>
                {price > 0 ? `₹${price}` : 'FREE'}
              </span>
            </div>
            <div style={{ color: '#D4B574', fontSize: 14, letterSpacing: 0.5, marginBottom: 14 }}>
              {m.tagline}
            </div>
            <ul style={{ margin: 0, paddingLeft: 22, color: '#D8C8A4', fontSize: 15.5, lineHeight: 2 }}>
              {m.features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </button>
        );
      })}
      </div>

      <p style={{ color: '#B79F6D', fontSize: 11, marginTop: 16, textAlign: 'center', lineHeight: 1.5 }}>
        Admin verifies every signup. App access (Premium) is granted after approval.
      </p>
    </>,
    true,
  );

  if (step === STEP_PAY) {
    const amount      = tierPrice(tier) || 200;
    const upiId       = cfg?.upi_id || '';
    const payeeName   = cfg?.upi_payee_name || 'Team Fronx';
    const qrUrl       = cfg?.upi_qr_url;
    const note        = `TF Membership ${form.phone_number.trim()}`;
    // Standard UPI deep-link spec
    const upiLink = upiId
      ? `upi://pay?pa=${encodeURIComponent(upiId)}`
        + `&pn=${encodeURIComponent(payeeName)}`
        + `&am=${amount}`
        + `&cu=INR`
        + `&tn=${encodeURIComponent(note)}`
      : null;

    return wrap(
      <>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src="/logo.png" alt="" style={{ width: 64, height: 64, marginBottom: 8 }} />
          <h2 style={{ color: '#F7E8BF', fontSize: 20, fontWeight: 900 }}>
            Pay ₹{amount} to complete signup
          </h2>
          <p style={{ color: '#B79F6D', fontSize: 11, letterSpacing: 1 }}>
            Step 2 of 3
          </p>
        </div>

        {error && (
          <div style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: 'rgba(230,57,70,0.12)',
            border: '1px solid #E63946',
            borderRadius: 8,
            color: '#E63946', fontSize: 13,
          }}>{error}</div>
        )}

        {/* UPI ID */}
        <div style={{
          padding: 14,
          background: '#0F0F0F',
          border: '1px solid rgba(212, 181, 116, 0.25)',
          borderRadius: 10,
          marginBottom: 12,
        }}>
          <div style={{
            color: '#B79F6D', fontSize: 10,
            letterSpacing: 1.5, fontWeight: 700, marginBottom: 6,
          }}>UPI ID</div>
          <div style={{
            color: '#D4B574', fontFamily: 'monospace',
            fontSize: 16, fontWeight: 700, userSelect: 'all',
          }}>
            {upiId || '— not configured —'}
          </div>
        </div>

        {/* QR */}
        {qrUrl && (
          <div style={{
            padding: 14,
            background: '#fff',
            borderRadius: 10,
            marginBottom: 12,
            textAlign: 'center',
          }}>
            <img src={qrUrl} alt="QR"
              style={{ maxWidth: '100%', maxHeight: 220 }} />
            <div style={{ color: '#666', fontSize: 11, marginTop: 6 }}>
              Scan with any UPI app
            </div>
          </div>
        )}

        {/* Pay button (deep-link) — works on mobile */}
        {upiLink && (
          <a href={upiLink}
            style={{
              display: 'block',
              padding: '14px',
              background: '#D4B574',
              color: '#000',
              borderRadius: 10,
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 900,
              letterSpacing: 0.5,
              textDecoration: 'none',
              marginBottom: 14,
            }}>
            📱 Pay ₹{amount} via UPI App
          </a>
        )}

        {/* Screenshot upload */}
        <div style={{
          padding: 14,
          background: 'rgba(212, 181, 116, 0.06)',
          border: '1px solid rgba(212, 181, 116, 0.22)',
          borderRadius: 10,
          marginBottom: 14,
        }}>
          <div style={{ color: '#D4B574', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>
            UPLOAD PAYMENT SCREENSHOT
          </div>

          {shotPreview ? (
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
              <img src={shotPreview} alt="" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', background: '#000', display: 'block' }} />
              <button type="button" onClick={() => { setShotFile(null); setShotPreview(''); if (shotInputRef.current) shotInputRef.current.value = ''; }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  padding: '6px 12px',
                  background: 'rgba(0,0,0,0.75)',
                  border: '1px solid #E63946',
                  borderRadius: 6, color: '#E63946',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>Remove</button>
            </div>
          ) : (
            <button type="button" onClick={() => shotInputRef.current?.click()}
              style={{
                width: '100%', padding: '24px 16px',
                background: '#0F0F0F',
                border: '2px dashed rgba(212, 181, 116, 0.35)',
                borderRadius: 8, color: '#D8C8A4',
                fontSize: 13, cursor: 'pointer',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 6,
              }}>
              <span style={{ fontSize: 30 }}>🧾</span>
              <span style={{ fontWeight: 700 }}>Click to attach screenshot</span>
              <span style={{ color: '#B79F6D', fontSize: 11 }}>JPG/PNG · max 5 MB</span>
            </button>
          )}
          <input ref={shotInputRef} type="file" accept="image/*"
            style={{ display: 'none' }} onChange={handleShotChange} />
        </div>

        <button onClick={submitScreenshot}
          disabled={!shotFile || uploading}
          style={{
            width: '100%', padding: '14px',
            background: (!shotFile || uploading) ? '#5A4220' : '#D4B574',
            color: '#000', border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 900, letterSpacing: 1,
            cursor: (!shotFile || uploading) ? 'wait' : 'pointer',
          }}>
          {uploading
            ? `Uploading… ${shotProgress}%`
            : 'SUBMIT FOR VERIFICATION'}
        </button>

        <p style={{ color: '#B79F6D', fontSize: 11, marginTop: 10, textAlign: 'center', lineHeight: 1.5 }}>
          Admin will verify within 24 hours and contact you with the WhatsApp link.
        </p>
      </>
    );
  }

  return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <img src="/logo.png" alt="" style={{ width: 60, height: 60, marginBottom: 6 }} />
        <h1 style={{
          color: '#F7E8BF', fontSize: 20, fontWeight: 900,
          marginBottom: 4, letterSpacing: -0.3,
        }}>
          Join Team Fronx
        </h1>
        <p style={{ color: '#B79F6D', fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }}>
          Step 2 of 3 · Your details
        </p>
        {/* Selected tier chip */}
        <div style={{
          display: 'inline-block',
          marginTop: 10,
          padding: '4px 12px',
          background: 'rgba(212,181,116,0.12)',
          border: '1px solid rgba(212,181,116,0.3)',
          borderRadius: 20,
          color: '#D4B574', fontSize: 11, fontWeight: 700,
        }}>
          {meta.label} · {tierPrice(tier) > 0 ? `₹${tierPrice(tier)}` : 'FREE'}
          {' '}
          <button type="button"
            onClick={() => setStep(STEP_TIER)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#B79F6D',
              fontSize: 11,
              cursor: 'pointer',
              padding: 0,
              marginLeft: 4,
              textDecoration: 'underline',
            }}>change</button>
        </div>
      </div>

      {error && (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          background: 'rgba(230,57,70,0.12)',
          border: '1px solid #E63946',
          borderRadius: 8,
          color: '#E63946', fontSize: 13,
        }}>{error}</div>
      )}

      <form onSubmit={handleSubmitForm} noValidate>
        <SectionLabel>Profile Photo</SectionLabel>
        <div style={{ marginBottom: 12 }}>
          <input ref={photoInputRef} type="file" accept="image/*"
            onChange={handlePhotoChange} style={{ display: 'none' }} />
          {photoPreview ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: 10,
              background: '#0F0F0F',
              border: '1px solid rgba(212,181,116,0.25)',
              borderRadius: 10,
            }}>
              <img src={photoPreview} alt="Profile preview"
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #D4B574',
                }} />
              <div style={{ flex: 1, fontSize: 12, color: '#D8C8A4' }}>
                <div style={{ fontWeight: 700 }}>{photoFile?.name}</div>
                <div style={{ color: '#9A9A9A', fontSize: 11 }}>
                  {photoFile && (photoFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <button type="button" onClick={clearPhoto}
                style={{
                  background: 'transparent', color: '#E63946',
                  border: '1px solid #E63946', borderRadius: 6,
                  padding: '6px 10px', fontSize: 11, cursor: 'pointer',
                }}>Remove</button>
            </div>
          ) : (
            <button type="button"
              onClick={() => photoInputRef.current?.click()}
              style={{
                width: '100%', padding: '20px',
                background: '#0F0F0F',
                color: '#D4B574',
                border: `1.5px dashed ${errors.profile_photo ? '#E63946' : 'rgba(212,181,116,0.4)'}`,
                borderRadius: 10, fontSize: 13,
                fontWeight: 600, cursor: 'pointer',
              }}>
              📷 Click to upload photo <span style={{ color: '#E63946' }}>*</span>
              <div style={{ color: '#9A9A9A', fontSize: 11, fontWeight: 400, marginTop: 4 }}>
                JPG / PNG, max 5 MB
              </div>
            </button>
          )}
          {errors.profile_photo && (
            <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>
              {errors.profile_photo}
            </p>
          )}
        </div>

        <SectionLabel>Personal</SectionLabel>
        <In label="Full Name" name="name" required error={errors.name}
            value={form.name} onChange={handleChange}
            placeholder="e.g. Arjun Menon" />
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
            Phone Number <span style={{ color: '#E63946' }}>*</span>
          </label>
          <div style={{ marginTop: 4 }}>
            <PhoneInput
              value={form.phone_number}
              onChange={(v) => handleChange({ target: { name: 'phone_number', value: v } })}
              error={!!errors.phone_number}
              inputProps={{ name: 'phone_number' }}
            />
          </div>
          {errors.phone_number && (
            <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>
              {errors.phone_number}
            </p>
          )}
        </div>
        <In label="Email" name="email" type="email" required
            value={form.email} onChange={handleChange}
            placeholder="you@example.com" error={errors.email} />

        <SectionLabel>Chapter</SectionLabel>
        <Sel label="District" name="district" required
             value={form.district} onChange={handleChange}
             options={KERALA_DISTRICTS} placeholder="Select district…"
             error={errors.district} />

        <SectionLabel>Vehicle</SectionLabel>
        <In label="Vehicle Number" name="vehicle_number" required
            style={{ textTransform: 'uppercase' }}
            value={form.vehicle_number} onChange={handleChange}
            placeholder="KL-11-AA-1234" error={errors.vehicle_number} />
        <Sel label="Fronx Variant" name="car_variant" required
             value={form.car_variant} onChange={handleChange}
             options={FRONX_VARIANTS} placeholder="Select variant…"
             error={errors.car_variant} />
        <Sel label="Engine Variant" name="engine_variant" required
             value={form.engine_variant} onChange={handleChange}
             options={ENGINE_VARIANTS} placeholder="Select engine…"
             error={errors.engine_variant} />
        <Sel label="Car Colour" name="car_color" required
             value={form.car_color} onChange={handleChange}
             options={FRONX_COLORS} placeholder="Select colour…"
             error={errors.car_color} />

        <SectionLabel>Emergency</SectionLabel>
        <Sel label="Blood Group" name="blood_group" required
             value={form.blood_group} onChange={handleChange}
             options={BLOOD_GROUPS} placeholder="Select…"
             error={errors.blood_group} />
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
            Emergency Contact <span style={{ color: '#E63946' }}>*</span>
          </label>
          <div style={{ marginTop: 4 }}>
            <PhoneInput
              value={form.emergency_contact}
              onChange={(v) => handleChange({ target: { name: 'emergency_contact', value: v } })}
              error={!!errors.emergency_contact}
              inputProps={{ name: 'emergency_contact' }}
            />
          </div>
          {errors.emergency_contact && (
            <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>
              {errors.emergency_contact}
            </p>
          )}
        </div>

        {/* Shipping address — only for Standard / Premium */}
        {meta?.needsShipping && (
          <>
            <SectionLabel>Shipping Address (for logo)</SectionLabel>
            <In label="House / Flat No." name="house_no" required
                value={form.house_no} onChange={handleChange}
                placeholder="e.g. 4B / Flat 203"
                error={errors.house_no} />
            <In label="Street / Area" name="address_line" required
                value={form.address_line} onChange={handleChange}
                placeholder="Street, locality"
                error={errors.address_line} />
            <In label="Landmark" name="landmark" required
                value={form.landmark} onChange={handleChange}
                placeholder="e.g. Opposite SBI ATM"
                error={errors.landmark} />
            <In label="City" name="city" required
                value={form.city} onChange={handleChange}
                placeholder="Kollam"
                error={errors.city} />
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
                State <span style={{ color: '#E63946' }}>*</span>
              </label>
              <select name="state" value={form.state} onChange={handleChange}
                style={{
                  width: '100%', padding: '10px 12px', marginTop: 4,
                  background: '#0F0F0F', color: '#F7E8BF',
                  border: `1px solid ${errors.state ? '#E63946' : 'rgba(212,181,116,0.2)'}`,
                  borderRadius: 8, fontSize: 14,
                }}>
                <option value="">Select state…</option>
                {INDIAN_STATES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {errors.state && (
                <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>
                  {errors.state}
                </p>
              )}
            </div>
            <In label="PIN Code" name="pincode" required
                value={form.pincode} onChange={handleChange}
                placeholder="691001"
                error={errors.pincode} />
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
                Alternate Phone <span style={{ color: '#E63946' }}>*</span>
              </label>
              <div style={{ marginTop: 4 }}>
                <PhoneInput
                  value={form.alternate_phone}
                  onChange={(v) => handleChange({ target: { name: 'alternate_phone', value: v } })}
                  error={!!errors.alternate_phone}
                  inputProps={{ name: 'alternate_phone' }}
                />
              </div>
              {errors.alternate_phone && (
                <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>
                  {errors.alternate_phone}
                </p>
              )}
            </div>
          </>
        )}

        <div style={{
          margin: '20px 0',
          padding: 14,
          background: 'rgba(212, 181, 116, 0.06)',
          borderRadius: 10,
          border: '1px solid rgba(212, 181, 116, 0.25)',
          textAlign: 'center',
        }}>
          <div style={{ color: '#B79F6D', fontSize: 11, letterSpacing: 1, marginBottom: 4 }}>
            {meta?.label?.toUpperCase()} · TOTAL
          </div>
          <div style={{ color: '#F7E8BF', fontSize: 28, fontWeight: 900 }}>
            {tierPrice(tier) > 0 ? `₹${tierPrice(tier)}` : 'FREE'}
          </div>
          {tierPrice(tier) > 0 && (
            <div style={{ color: '#B79F6D', fontSize: 10, marginTop: 4 }}>
              Pay via UPI on the next step
            </div>
          )}
        </div>

        <button type="submit" disabled={submitting}
          style={{
            width: '100%', padding: '16px',
            background: submitting ? '#5A4220' : '#D4B574',
            color: '#000', border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 900, letterSpacing: 1,
            cursor: submitting ? 'wait' : 'pointer',
          }}>
          {submitting
            ? (photoProgress > 0 && photoProgress < 100
                ? `Uploading photo… ${photoProgress}%`
                : 'Saving…')
            : (tierPrice(tier) > 0 ? 'CONTINUE TO PAYMENT →' : 'SUBMIT APPLICATION →')}
        </button>
      </form>
    </>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      marginTop: 14, marginBottom: 8,
      color: '#D4B574', fontSize: 10,
      letterSpacing: 1.8, fontWeight: 700, textTransform: 'uppercase',
    }}>{children}</div>
  );
}
function In({ label, error, required, ...rest }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
        {label}{required && <span style={{ color: '#E63946' }}> *</span>}
      </label>
      <input {...rest}
        style={{
          width: '100%', padding: '10px 12px', marginTop: 4,
          background: '#0F0F0F', color: '#F7E8BF',
          border: `1px solid ${error ? '#E63946' : 'rgba(212,181,116,0.2)'}`,
          borderRadius: 8, fontSize: 14,
          ...(rest.style || {}),
        }} />
      {error && <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>{error}</p>}
    </div>
  );
}
function Sel({ label, error, required, options, placeholder, ...rest }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
        {label}{required && <span style={{ color: '#E63946' }}> *</span>}
      </label>
      <select {...rest}
        style={{
          width: '100%', padding: '10px 12px', marginTop: 4,
          background: '#0F0F0F', color: '#F7E8BF',
          border: `1px solid ${error ? '#E63946' : 'rgba(212,181,116,0.2)'}`,
          borderRadius: 8, fontSize: 14,
        }}>
        <option value="">{placeholder || 'Select…'}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p style={{ color: '#E63946', fontSize: 11, marginTop: 4 }}>{error}</p>}
    </div>
  );
}
